import { createMuiTheme as _createMuiTheme, Theme } from '@material-ui/core';

// Copilot gradient identity — locked-in brand direction, ported from
// feature/react-19 (the AI source of truth). Same 8 token values; lives
// under theme.dado.copilot so it flows through both the MUI theme and
// the emotion `combined` theme (provider.tsx spreads ...muiTheme) and
// stays type-safe at call sites.
const dadoTokens = {
  copilot: {
    gradientHorizontal: 'linear-gradient(90deg, #0E78E4, #7856FF)',
    gradientDiagonal: 'linear-gradient(135deg, #0E78E4 0%, #7856FF 100%)',
    purpleAccent: '#7856FF',
    purpleSurface: '#F4F1FF',
    blueBanner: '#E7F2FD',
    blueBannerBorder: '#BFDAF9',
    pillShadow: '0 1px 8px rgba(120,86,255,.42)',
    sendButtonShadow: '0 2px 6px rgba(120,86,255,.4)'
  }
} as const;

export type DadoTokens = typeof dadoTokens;

// Augment the v4 MUI Theme so theme.dado is typed everywhere — including
// emotion styled components, since FullTheme (themes/index.ts) is
// `Theme & IEMotionTheme`.
declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    dado: DadoTokens;
  }
  interface ThemeOptions {
    dado?: DadoTokens;
  }
}

export const MUI_CHECKBOX_SIZE = 20;
export const createMuiTheme = (
  overrideBaseThemeFn: (baseTheme: Theme) => Theme = (t) => t,
  variant: 'ADMIN_APP' | 'EMPLOYEE_APP' = 'ADMIN_APP'
): Theme => {
  const bpSm = 600;
  const theme = overrideBaseThemeFn(
    _createMuiTheme({
      breakpoints: {
        values:
          variant === 'ADMIN_APP'
            ? {
                xs: 0,
                sm: bpSm,
                md: 960,
                lg: 1200, // set 1080 keep this - add a fourth breakpoint for the thrid column, adjust task details page to keep the space of the third column alive
                xl: 1920
              }
            : {
                xs: 0,
                sm: bpSm,
                md: 1080,
                lg: 1400,
                xl: 1800
              }
      },
      typography: {
        fontFamily: 'ebony, sans-serif',
        fontSize: 16,
        fontWeightLight: 200,
        fontWeightRegular: 200,
        fontWeightMedium: 400,
        fontWeightBold: 400,
        body1: {
          fontSize: '0.9375rem', // 15px
          lineHeight: '1.25rem', // 20px
          fontWeight: 200,
          '@media (min-width: 1281px)': {
            fontSize: '1rem',
            lineHeight: '1.375rem' // 22px
          }
        },
        body2: {
          fontSize: '0.9375rem', // 15px
          lineHeight: '1.25rem', // 20px
          fontWeight: 200,
          '@media (min-width: 1281px)': {
            fontSize: '1rem',
            lineHeight: '1.375rem' // 22px
          }
        },
        caption: {
          fontSize: '0.8125rem', // 15px
          lineHeight: '1.1875rem', // 18px
          fontWeight: 200,
          color: '#778088',
          '@media (min-width: 1281px)': {
            fontSize: '0.875rem' // 14px
          }
        },
        h1: {
          fontSize: '2.5rem', // 40px
          lineHeight: '3rem', // 48px
          fontWeight: 400,
          letterSpacing: '-0.01em',
          [`@media (max-width: ${bpSm}px)`]: {
            fontSize: '2.5rem',
            lineHeight: '3rem'
          }
        },
        h2: {
          fontSize: '2rem', // 32px
          lineHeight: '2.5rem', // 40px
          fontWeight: 400,
          [`@media (max-width: ${bpSm}px)`]: {
            fontSize: '1.75rem',
            lineHeight: '2rem'
          }
        },
        h3: {
          fontSize: '1.75em', // 28px
          lineHeight: '2.25rem', // 36px
          fontWeight: 400,
          [`@media (max-width: ${bpSm}px)`]: {
            fontSize: '1.5rem', // 24px
            lineHeight: '1.75rem' // 28px
          }
        },
        h4: {
          fontSize: '1.375rem', // 22px
          lineHeight: '1.875rem', // 30px
          fontWeight: 400,
          [`@media (max-width: ${bpSm}px)`]: {
            fontSize: '1.25rem', // 20px
            lineHeight: '1.5rem' // 24px
          }
        },
        h5: {
          fontSize: '1.125rem', // 18px
          lineHeight: '1.5rem', // 24px
          fontWeight: 400,
          '@media (min-width: 1281px)': {
            fontSize: '1.1875rem', // 19px
            lineHeight: '1.6rem' // 24px
          }
        },
        h6: {
          fontSize: '0.9375rem', // 15px
          lineHeight: '1.375rem', // 22px
          fontWeight: 400,
          '@media (min-width: 1281px)': {
            fontSize: '1rem' // 16px
          }
        }
      },
      palette: {
        common: {
          black: '#08121a'
        },
        text: {
          primary: '#08121a',
          secondary: '#778088',
          disabled: '#bbc1c7',
          hint: '#778088'
        },
        grey: {
          100: '#fafafa',
          200: '#ebedef',
          300: '#dde0e3',
          400: '#bbc1c7',
          500: '#778088',
          600: '#5f676e',
          700: '#494f55',
          800: '3a4147',
          900: '#272c30'
        },
        primary: {
          light: '#6ab2fb',
          main: '#0e78e4',
          dark: '#0454a4',
          contrastText: '#ffffff'
        },
        secondary: {
          light: '#f77b6e',
          main: '#e25e50',
          dark: '#cb3120',
          contrastText: '#ffffff'
        },
        action: {
          disabled: '#bbc1c7',
          disabledBackground: '#ebedef',
          disabledOpacity: 1
        }
      },
      shape: {
        borderRadius: 6
      }
    })
  );

  return {
    ...theme,
    dado: dadoTokens,
    overrides: {
      MuiCssBaseline: {
        '@global': {
          strong: {
            fontWeight: 400
          }
        }
      },
      MuiCard: {
        root: {
          padding: theme.spacing(3),
          transition: 'all ease .2s',

          '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 4px ${theme.palette.primary.main}`
          }
        }
      },
      MuiCardActions: {
        root: {
          padding: theme.spacing(2)
        }
      },
      MuiDialog: {
        paperWidthSm: {
          maxWidth: 650
        }
      },
      MuiDialogTitle: {
        root: {
          paddingTop: theme.spacing(4),
          paddingRight: theme.spacing(4),
          paddingBottom: theme.spacing(2),
          paddingLeft: theme.spacing(4)
        }
      },
      MuiDialogContent: {
        root: {
          paddingTop: theme.spacing(2),
          paddingRight: theme.spacing(4),
          paddingBottom: theme.spacing(3),
          paddingLeft: theme.spacing(4)
        }
      },
      MuiDialogContentText: {
        root: {
          fontSize: '0.9375rem',
          color: theme.palette.common.black
        }
      },
      MuiDialogActions: {
        root: {
          color: theme.palette.common.black,
          paddingTop: theme.spacing(3),
          paddingRight: theme.spacing(4),
          paddingBottom: theme.spacing(3),
          paddingLeft: theme.spacing(4)
        }
      },
      MuiButtonBase: {
        root: {
          fontFamily: 'ebony,sans-serif',
          fontWeight: 200
        }
      },
      MuiButton: {
        root: {
          padding: `${theme.spacing(1.25)}px ${theme.spacing(1.75)}px`,
          color: theme.palette.grey[700],
          fontSize: '0.9375rem', // 15px
          fontWeight: 200,
          whiteSpace: 'nowrap',
          minWidth: 'unset',
          lineHeight: '1.25rem', // 20px
          '@media (min-width: 1281px)': {
            fontSize: '1rem'
          },

          '&:hover': {
            backgroundColor: 'rgba(119,128,136,.1)'
          },
          '&:focus': {
            boxShadow: 'none'
          }
        },
        label: {
          height: 20,
          textTransform: 'initial'
        },
        text: {
          padding: `${theme.spacing(1.25)}px ${theme.spacing(1.75)}px`
        },
        textSizeSmall: {
          '&&': {
            padding: 0,
            fontSize: '0.9375rem',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline'
            }
          }
        },
        sizeSmall: {
          fontSize: '0.8125rem',
          padding: `${theme.spacing(0.75)}px ${theme.spacing(1.25)}px`
        },
        startIcon: {
          '&[class*=MuiButton-iconSizeSmall]': {
            marginLeft: 0,
            marginRight: theme.spacing(0.5)
          }
        },
        contained: {
          fontWeight: 400,
          color: theme.palette.grey[700],
          backgroundColor: theme.palette.grey[300],
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&:focus': {
            boxShadow: 'none'
          },
          '&:active': {
            boxShadow: 'none'
          }
        },
        containedSizeSmall: {
          padding: `${theme.spacing()}px ${theme.spacing(1.5)}px`
        },
        containedPrimary: {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          },
          '&:active': {
            backgroundColor: theme.palette.primary.light,
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          }
        },
        containedSecondary: {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.secondary.main,
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
            boxShadow: '0 0 0 4px rgba(247,123,110,.1)'
          },
          '&:active': {
            backgroundColor: '#FDBCB4',
            boxShadow: '0 0 0 4px rgba(247,123,110,.1)'
          }
        },
        outlined: {
          color: theme.palette.grey[700],
          borderColor: theme.palette.grey[300],
          padding: `calc(${theme.spacing(1.25)}px - 1px) ${theme.spacing(2)}px`,

          '&:hover': {
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&:active': {
            backgroundColor: '#FAFAFA',
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          }
        },
        outlinedPrimary: {
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,

          '&:hover': {
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          },
          '&:active': {
            backgroundColor: '#C1DEFB',
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          }
        },
        outlinedSecondary: {
          color: theme.palette.secondary.main,
          borderColor: theme.palette.secondary.main,

          '&:hover': {
            boxShadow: '0 0 0 4px rgba(247,123,110,.1)'
          },
          '&:active': {
            backgroundColor: '#FDBCB4',
            boxShadow: '0 0 0 4px rgba(247,123,110,.1)'
          },
          '&[class*=Mui-disabled]': {
            borderColor: theme.palette.grey[200]
          }
        }
      },
      MuiButtonGroup: {
        groupedContainedPrimary: {
          ':not(:last-child)': {}
        }
      },
      MuiChip: {
        root: {
          color: theme.palette.text.primary,
          fontSize: '0.9375rem',
          backgroundColor: theme.palette.grey[200],
          height: theme.spacing(3.5)
        },
        label: {
          lineHeight: '0.9375rem',
          letterSpacing: 0.2
        },
        labelSmall: {
          fontSize: '0.8125rem',
          paddingRight: theme.spacing(1.5),
          paddingLeft: theme.spacing(1.5)
        }
      },
      MuiTabs: {
        root: {
          backgroundColor: 'transparent',
          borderBottomWidth: 1,
          borderBottomColor: theme.palette.grey[300],
          borderBottomStyle: 'solid'
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0
        }
      },
      MuiTab: {
        root: {
          fontWeight: 400,
          textTransform: 'initial',
          minWidth: 0,
          [theme.breakpoints.up('md')]: {
            minWidth: 0
          }
        },
        textColorPrimary: {
          color: theme.palette.grey[500]
        },
        selected: {
          zIndex: 1,
          backgroundColor: 'transparent'
        },
        wrapped: {
          fontSize: '0.9375rem'
        }
      },
      MuiIconButton: {
        root: {
          color: theme.palette.grey[500],
          padding: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.grey[900]
          }
        },
        colorPrimary: {
          '&:hover': {
            backgroundColor: 'transparent'
          }
        }
      },
      MuiSvgIcon: {
        fontSizeSmall: {
          fontSize: '16px'
        }
      },
      MuiTooltip: {
        tooltip: {
          fontSize: '0.9375rem',
          fontWeight: theme.typography.fontWeightLight,
          color: theme.palette.common.white,
          backgroundColor: theme.palette.common.black,
          borderRadius: 4,
          margin: 0
        },
        arrow: {
          color: theme.palette.common.black,
          margin: 0
        },
        tooltipPlacementTop: {
          margin: `${theme.spacing()}px 0 !important`
        },
        tooltipPlacementBottom: {
          margin: `${theme.spacing()}px 0 !important`
        },
        tooltipPlacementLeft: {
          margin: `0 ${theme.spacing()}px !important`
        },
        tooltipPlacementRight: {
          margin: `0 ${theme.spacing()}px !important`
        }
      },
      MuiDivider: {
        root: {
          backgroundColor: theme.palette.grey[700]
        },
        light: {
          backgroundColor: theme.palette.grey[300] // gray.w300
        }
      },
      MuiListItemIcon: {
        root: {
          minWidth: 'unset',
          marginRight: theme.spacing(1)
        }
      },
      MuiAvatar: {
        root: {
          width: 32,
          height: 32
        }
      },
      MuiTable: {
        stickyHeader: {
          backgroundColor: theme.palette.background.paper
        }
      },
      MuiTableCell: {
        stickyHeader: {
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0px 1px 0px 0px rgba(0,0,0,0.1)'
        }
      },
      // @ts-ignore
      MuiToggleButtonGroup: {
        root: {
          borderRadius: 4
        }
      },
      MuiFormLabel: {
        root: {
          color: theme.palette.text.primary
        }
      },
      MuiFormHelperText: {
        contained: {
          marginLeft: 0,
          marginRight: 0
        }
      },
      MuiToggleButton: {
        root: {
          color: theme.palette.text.primary,
          padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
          '&:hover': {
            backgroundColor: theme.palette.grey[200]
          },
          '&[class*=Mui-selected]': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main
          },
          '&[class*=Mui-selected]:hover': {
            color: `${theme.palette.primary.contrastText} !important`,
            backgroundColor: `${theme.palette.primary.dark} !important`
          }
        }
      },
      MuiFormControlLabel: {
        root: {
          marginLeft: 0,
          // this styles are needed to show a hover state on the checkbox when hovering the label
          '&:hover [class*=MuiCheckbox-root]': {
            backgroundColor: theme.palette.grey[100],
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&:hover [class*=MuiCheckbox-root][class*=Mui-checked]': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          },
          '&:hover [class*=MuiCheckbox-root][class*=Mui-disabled]': {
            backgroundColor: theme.palette.grey[200],
            boxShadow: 'none'
          },
          '&:hover [class*=MuiCheckbox-root][class*=Mui-checked.Mui-disabled]': {
            backgroundColor: theme.palette.grey[300],
            boxShadow: 'none'
          }
        }
      },
      MuiInputBase: {
        root: {
          fontSize: '0.9375rem',
          lineHeight: `${theme.spacing(2.5)}px`,
          borderColor: theme.palette.grey[300],

          '&[class*=Mui-disabled]': {
            borderColor: theme.palette.grey[300],
            background: theme.palette.grey[100]
          }
        },
        input: {
          height: theme.spacing(2.5),
          fontVariantNumeric: 'tabular-nums',

          '&[type="time" i]::-webkit-calendar-picker-indicator': {
            marginLeft: 4,
            paddingRight: 12
          }
        },
        inputMarginDense: {
          fontSize: '0.9375rem',
          height: theme.spacing(2)
        },
        inputTypeSearch: {
          '&&': {
            paddingRight: theme.spacing()
          }
        },
        multiline: {
          lineHeight: `${theme.spacing(3)}px`
        }
      },
      // Mind that if you change this, you might also have to adapt
      // the style overrides for our MarkdownEditor!
      MuiOutlinedInput: {
        root: {
          backgroundColor: theme.palette.common.white,

          '&:hover [class*=MuiOutlinedInput-notchedOutline]': {
            borderColor: theme.palette.grey[400],
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&:hover[class*=Mui-focused] [class*=MuiOutlinedInput-notchedOutline]': {
            borderColor: theme.palette.primary.main
          },
          '&[class*=Mui-focused] [class*=MuiOutlinedInput-notchedOutline]': {
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          },
          '&[class*=Mui-focused] [class*=MuiOutlinedInput-input]': {
            borderColor: theme.palette.primary.main
          },
          '&[class*=Mui-disabled] [class*=MuiOutlinedInput-notchedOutline]': {
            borderColor: theme.palette.grey[300]
          },
          '&[class*=Mui-disabled]:hover [class*=MuiOutlinedInput-notchedOutline]': {
            boxShadow: 'none'
          },
          '&[class*=Mui-disabled]': {
            borderColor: theme.palette.grey[300]
          }
        },
        input: {
          padding: `${theme.spacing(1.25)}px 0 ${theme.spacing(
            1.25
          )}px ${theme.spacing(1.5)}px`,

          '&[type=search]::-webkit-search-cancel-button': {
            position: 'relative',
            right: theme.spacing(1.5),
            appearance: 'none',
            height: 20,
            width: 20,
            borderRadius: 10,
            color: 'red',
            backgroundColor: theme.palette.grey[300],
            backgroundImage:
              'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXIteCI+PGxpbmUgeDE9IjE4IiB5MT0iNiIgeDI9IjYiIHkyPSIxOCI+PC9saW5lPjxsaW5lIHgxPSI2IiB5MT0iNiIgeDI9IjE4IiB5Mj0iMTgiPjwvbGluZT48L3N2Zz4=")',
            backgroundSize: 14,
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            cursor: 'pointer'
          }
        },
        inputMarginDense: {
          paddingTop: theme.spacing(),
          paddingBottom: theme.spacing()
        },
        multiline: {
          padding: `${theme.spacing(1.25)}px ${theme.spacing(1.5)}px`
        },
        notchedOutline: {
          borderColor: theme.palette.grey[300]
        },
        adornedEnd: {
          paddingRight: `${theme.spacing(1.5)}px`
        },
        adornedStart: {
          paddingLeft: `${theme.spacing(1.5)}px`
        }
      },
      MuiInputLabel: {
        outlined: {
          fontSize: '0.9375rem',
          color: theme.palette.grey[500],
          backgroundColor: theme.palette.background.paper,
          transform: 'translate(14px, 12px) scale(1)',
          '@media (min-width: 1281px)': {
            transform: 'translate(14px, 9px) scale(1)'
          },
          '&[class*=MuiInputLabel-shrink]': {
            transform: 'translate(14px, -6px) scale(.75)',
            '@media (min-width: 1281px)': {
              transform: 'translate(14px, -8px) scale(.75)'
            }
          },

          '&[class*=MuiInputLabel-marginDense]': {
            transform: 'translate(14px, 8px) scale(1)',
            '@media (min-width: 1281px)': {
              transform: 'translate(14px, 5px) scale(1)'
            },
            '&[class*=MuiInputLabel-shrink]': {
              transform: 'translate(14px, -6px) scale(.75)',
              '@media (min-width: 1281px)': {
                transform: 'translate(14px, -8px) scale(.75)'
              }
            }
          },
          // Hide label background if label is in the field. This avoids boxes over disabled fields.
          '&:not(.MuiFormLabel-filled)': {
            backgroundColor: 'transparent !important'
          }
        }
      },
      MuiSelect: {
        iconOutlined: {
          top: 8
        }
      },
      MuiAutocomplete: {
        inputRoot: {
          '&&[class*="MuiOutlinedInput-root"]': {
            padding: `0 ${theme.spacing(1.25)}px`
          },
          '&&[class*="MuiOutlinedInput-root"] [class*=MuiAutocomplete-input]': {
            padding: `${theme.spacing(1.25)}px 0`
          },

          '&&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
            paddingLeft: theme.spacing(0)
          }
        },
        hasPopupIcon: {
          '&&[class*=MuiAutocomplete-hasClearIcon] [class*=MuiAutocomplete-inputRoot][class*="MuiOutlinedInput-root"]': {
            paddingRight: theme.spacing(6)
          }
        },
        tag: {
          marginTop: 4,
          marginBottom: 0
        },
        option: {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          '&[data-selected="true"]': {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.common.black,

            '& [class*="MuiButton-root"]': {
              color: theme.palette.common.black
            },
            '& [class*="MuiIcon-root"]': {
              color: theme.palette.common.black
            }
          },
          '&[data-focus="true"]': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,

            '& [class*="MuiButton-root"]': {
              color: theme.palette.common.white
            },
            '& [class*="MuiIcon-root"]': {
              color: theme.palette.common.white
            },
            '& [class*="MuiTypography-caption"]': {
              color: theme.palette.common.white
            }
          }
        },
        groupUl: {
          '&.MuiAutomomplete-option': {
            paddingLeft: theme.spacing(2)
          }
        }
      },
      MuiPickersInlineWrapper: {
        popoverPaper: {
          width: 'auto',
          paddingBottom: 0
        }
      },
      MuiPickersToolbar: {
        toolbarLandscape: {
          maxWidth: 'unset',
          justifyContent: 'center'
        }
      },
      MuiPickersTimePickerToolbar: {
        hourMinuteLabel: {
          alignItems: 'center'
        }
      },
      MuiPickersToolbarText: {
        toolbarTxt: {
          fontVariantNumeric: 'tabular-nums'
        }
      },
      MuiListItem: {
        root: {
          '&[class*=Mui-selected]': {
            backgroundColor: `${theme.palette.primary.main}!important`,
            color: `${theme.palette.common.white}`,
            fontWeight: 400
          }
        }
      },
      MuiLink: {
        root: {
          fontFamily: 'ebony, sans-serif',
          fontWeight: 200,
          fontSize: '0.9375rem'
        }
      },
      MuiCheckbox: {
        root: {
          display: 'block',
          border: `1px solid ${theme.palette.grey[300]}`,
          height: MUI_CHECKBOX_SIZE,
          width: MUI_CHECKBOX_SIZE,
          padding: 0,
          borderRadius: 2,

          '&::after': {
            display: 'block',
            content: '""',
            height: 18,
            width: 18,
            position: 'relative',
            top: 0,
            left: 0,
            background: `url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m4 11 3.5 3.5L16 6' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            transform: 'scale(.5)',
            opacity: 0
          },

          '& [class*=MuiSvgIcon-root]': {
            display: 'none'
          },
          '&[class*=MuiIconButton-root]:hover': {
            backgroundColor: theme.palette.grey[100],
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&+[class*=MuiFormControlLabel-label]': {
            marginLeft: theme.spacing(1)
          },

          '&[class*=Mui-checked]': {
            backgroundColor: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.dark}`,

            '&::after': {
              transform: 'scale(1)',
              opacity: 1,
              transition: 'all ease-out .15s'
            },

            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
            }
          },
          '&[class*=Mui-disabled]': {
            backgroundColor: theme.palette.grey[200],
            borderColor: theme.palette.grey[300],
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
              boxShadow: 'none'
            }
          },
          '&[class*=Mui-checked.Mui-disabled]': {
            backgroundColor: theme.palette.grey[300],
            content: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' stroke='%23ebedef' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 6 9 17l-5-5'/%3E%3C/svg%3E")`,

            '&:hover': {
              backgroundColor: theme.palette.grey[300],
              boxShadow: 'none'
            }
          }
        }
      },
      MuiRadio: {
        root: {
          display: 'block',
          border: `1px solid ${theme.palette.grey[300]}`,
          height: 20,
          width: 20,
          padding: 0,
          borderRadius: 10,

          '&::after': {
            display: 'block',
            position: 'relative',
            top: 3,
            left: 3,
            content: '""',
            width: 12,
            height: 12,
            borderRadius: 6,
            margin: 0,
            backgroundColor: theme.palette.primary.main,
            transform: 'scale(.5)',
            opacity: 0
          },

          '& [class*=MuiSvgIcon-root]': {
            display: 'none'
          },

          '&[class*=MuiIconButton-root]:hover': {
            backgroundColor: theme.palette.grey[100],
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },

          '&[class*=Mui-checked]': {
            '&::after': {
              transform: 'scale(1)',
              opacity: 1,
              transition: 'all ease-out .1s'
            }
          },
          '&[class*=Mui-disabled]': {
            backgroundColor: theme.palette.grey[200],
            borderColor: theme.palette.grey[300],
            '&:hover': {
              backgroundColor: theme.palette.common.white,
              boxShadow: 'none'
            }
          },
          '&[class*=Mui-checked.Mui-disabled]': {
            backgroundColor: theme.palette.grey[200],
            '&::after': {
              backgroundColor: theme.palette.grey[400]
            },
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
              boxShadow: 'none'
            }
          },
          '&+[class*=MuiFormControlLabel-label]': {
            marginLeft: theme.spacing(1)
          }
        }
      },
      MuiSwitch: {
        root: {
          width: 58,
          height: 44,
          padding: theme.spacing(1.25)
        },
        switchBase: {
          padding: theme.spacing(1.5),
          '&:hover': {
            color: theme.palette.common.white,
            backgroundColor: 'transparent !important'
          },
          '&:hover + [class*=MuiSwitch-track]': {
            boxShadow: '0 0 0 4px rgba(119,128,136,.1)'
          },
          '&[class*=Mui-focusVisible] + [class*=MuiSwitch-track]': {
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          },
          '&[class*=Mui-checked]': {
            transform: 'translateX(14px)'
          },
          '&[class*=Mui-checked] + [class*=MuiSwitch-track]': {
            opacity: 1,
            backgroundColor: '#77D4A9 !important'
          },
          '&[class*=Mui-checked]:hover + [class*=MuiSwitch-track]': {
            boxShadow: '0 0 0 4px rgba(103,178,244,.1)'
          }
        },
        thumb: {
          width: 20,
          height: 20,
          backgroundColor: theme.palette.common.white,
          border: '1px solid rgba(0, 0, 0, .15)',
          boxShadow: 'none'
        },
        track: {
          opacity: 1,
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.grey[200],
          border: '1px solid rgba(0, 0, 0, .15)'
        }
      },
      MuiAccordionSummary: {
        root: {
          '&:hover': {
            backgroundColor: theme.palette.grey[100],
            minHeight: 'unset'
          },
          '&[class*=Mui-expanded]': {
            minHeight: 'unset'
          }
        },
        content: {
          margin: `${theme.spacing(1.5)}px 0`,
          '&[class*=Mui-expanded]': {
            margin: `${theme.spacing(1.5)}px 0`
          }
        }
      },
      MuiAccordionDetails: {
        root: {
          padding: `${theme.spacing()}px 0`
        }
      }
    },

    props: {
      MuiButtonBase: {
        disableRipple: true
      },
      MuiButtonGroup: {
        disableElevation: true,
        disableRipple: true
      },
      MuiTab: {
        disableRipple: true
      },
      MuiTextField: {
        variant: 'outlined'
      },
      MuiSelect: {
        variant: 'outlined'
      },
      MuiTooltip: {
        arrow: true,
        placement: 'top'
      },
      MuiSwitch: {
        edge: 'start'
      },
      MuiCheckbox: {
        color: 'default'
      }
    },
    mixins: {
      ...theme.mixins,
      toolbar: {
        minHeight: 48
      }
    }
  };
};
